import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api, { checkHealth, getDiagnosticInfo } from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminDiagnostic() {
  const { isAdmin, user } = useApp();
  const { t } = useI18n();
  const [diagnostics, setDiagnostics] = useState(null);
  const [healthResult, setHealthResult] = useState(null);
  const [signTest, setSignTest] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      runDiagnostics();
    }
  }, [isAdmin]);

  async function runDiagnostics() {
    setLoading(true);
    
    // Get client-side diagnostics
    const clientInfo = getDiagnosticInfo();
    
    // Run health check
    const health = await checkHealth();
    setHealthResult(health);
    
    // Test gateway/sign endpoint (dry run)
    let signEndpoint = null;
    try {
      // Just check if endpoint exists (OPTIONS or GET)
      const res = await api.get('/gateway/check/0').catch(() => null);
      signEndpoint = { available: true };
    } catch (e) {
      signEndpoint = { available: false, error: e.userMessage };
    }
    
    setDiagnostics({
      ...clientInfo,
      healthResult: health,
      signEndpoint,
    });
    
    setLoading(false);
  }

  async function testSignEndpoint() {
    setSignTest({ status: 'testing' });
    try {
      // This will fail but we capture the error type
      const res = await api.post('/gateway/sign', {
        company_id: 0,
        accepted_terms_1200: false,
      });
      setSignTest({ status: 'unexpected_success', data: res.data });
    } catch (e) {
      setSignTest({
        status: 'error',
        code: e.errorCode,
        type: e.errorType,
        message: e.userMessage,
        requestId: e.requestId,
        serverDetail: e.serverDetail,
      });
    }
  }

  if (!isAdmin) {
    return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  }

  const StatusBadge = ({ ok, label }) => (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      background: ok ? 'rgba(0,206,201,0.2)' : 'rgba(255,107,107,0.2)',
      color: ok ? '#00cec9' : '#ff6b6b',
    }}>
      {ok ? '✅' : '❌'} {label}
    </span>
  );

  return (
    <div className="page"><BackButton />
      <h1>🔧 Диагностика системы</h1>
      
      <button className="btn btn-primary" onClick={runDiagnostics} disabled={loading}>
        {loading ? '⏳ Проверка...' : '🔄 Запустить диагностику'}
      </button>

      {diagnostics && (
        <div className="diagnostic-results" style={{ marginTop: 20 }}>
          
          {/* Connection Info */}
          <div className="diag-section">
            <h3>🌐 Подключение</h3>
            <table className="diag-table">
              <tbody>
                <tr>
                  <td>API Base URL</td>
                  <td><code>{diagnostics.apiBaseUrl}</code></td>
                  <td>
                    <StatusBadge 
                      ok={diagnostics.apiBaseUrl?.startsWith('https://')} 
                      label={diagnostics.apiBaseUrl?.startsWith('https://') ? 'HTTPS' : 'NOT HTTPS!'} 
                    />
                  </td>
                </tr>
                <tr>
                  <td>WebApp Origin</td>
                  <td><code>{diagnostics.webappOrigin}</code></td>
                  <td></td>
                </tr>
                <tr>
                  <td>Telegram WebApp</td>
                  <td></td>
                  <td><StatusBadge ok={diagnostics.telegramAvailable} label={diagnostics.telegramAvailable ? 'Available' : 'Not available'} /></td>
                </tr>
                <tr>
                  <td>initData length</td>
                  <td>{diagnostics.initDataLength}</td>
                  <td><StatusBadge ok={diagnostics.initDataLength > 50} label={diagnostics.initDataLength > 50 ? 'OK' : 'Too short'} /></td>
                </tr>
                <tr>
                  <td>JWT Present</td>
                  <td></td>
                  <td><StatusBadge ok={diagnostics.jwtPresent} label={diagnostics.jwtPresent ? 'Yes' : 'No'} /></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Health Check */}
          <div className="diag-section">
            <h3>💓 Healthcheck API</h3>
            <table className="diag-table">
              <tbody>
                <tr>
                  <td>Status</td>
                  <td></td>
                  <td><StatusBadge ok={healthResult?.ok} label={healthResult?.ok ? 'OK' : 'FAIL'} /></td>
                </tr>
                <tr>
                  <td>Latency</td>
                  <td>{healthResult?.latency}ms</td>
                  <td><StatusBadge ok={healthResult?.latency < 1000} label={healthResult?.latency < 1000 ? 'Good' : 'Slow'} /></td>
                </tr>
                {healthResult?.error && (
                  <tr>
                    <td>Error</td>
                    <td colSpan="2" style={{ color: '#ff6b6b' }}>{healthResult.error}</td>
                  </tr>
                )}
                {healthResult?.data && (
                  <tr>
                    <td>Response</td>
                    <td colSpan="2"><code>{JSON.stringify(healthResult.data)}</code></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Sign Endpoint Test */}
          <div className="diag-section">
            <h3>✍️ Gateway Sign Endpoint</h3>
            <button className="btn btn-secondary" onClick={testSignEndpoint}>
              🧪 Тест endpoint /gateway/sign
            </button>
            {signTest && (
              <div style={{ marginTop: 12 }}>
                <table className="diag-table">
                  <tbody>
                    <tr>
                      <td>HTTP Code</td>
                      <td>{signTest.code || '—'}</td>
                      <td><StatusBadge ok={signTest.code === 400 || signTest.code === 403 || signTest.code === 422} label={signTest.code >= 400 && signTest.code < 500 ? 'Expected error' : signTest.code >= 500 ? 'Server error!' : 'OK'} /></td>
                    </tr>
                    <tr>
                      <td>Error Type</td>
                      <td>{signTest.type || '—'}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td>Message</td>
                      <td colSpan="2">{signTest.message}</td>
                    </tr>
                    {signTest.requestId && (
                      <tr>
                        <td>Request ID</td>
                        <td colSpan="2"><code>{signTest.requestId}</code></td>
                      </tr>
                    )}
                    {signTest.serverDetail && (
                      <tr>
                        <td>Server Detail</td>
                        <td colSpan="2" style={{ color: '#ff6b6b' }}>{signTest.serverDetail}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Current User */}
          <div className="diag-section">
            <h3>👤 Текущий пользователь</h3>
            <table className="diag-table">
              <tbody>
                <tr>
                  <td>User ID</td>
                  <td>{user?.id}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Telegram ID</td>
                  <td>{user?.telegram_id}</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Role</td>
                  <td>{user?.role}</td>
                  <td><StatusBadge ok={user?.role === 'admin'} label={user?.role} /></td>
                </tr>
                <tr>
                  <td>Profile Status</td>
                  <td>{user?.profile_status}</td>
                  <td><StatusBadge ok={user?.profile_status === 'verified'} label={user?.profile_status} /></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Timestamp */}
          <div style={{ marginTop: 20, fontSize: 12, opacity: 0.6, textAlign: 'center' }}>
            Диагностика от: {diagnostics.timestamp}
          </div>
        </div>
      )}

      <style>{`
        .diag-section {
          background: rgba(0,0,0,0.3);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }
        .diag-section h3 {
          margin: 0 0 12px 0;
          font-size: 16px;
        }
        .diag-table {
          width: 100%;
          font-size: 13px;
        }
        .diag-table td {
          padding: 8px 4px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          vertical-align: middle;
        }
        .diag-table tr:last-child td {
          border-bottom: none;
        }
        .diag-table code {
          background: rgba(108,92,231,0.2);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          word-break: break-all;
        }
      `}</style>
    </div>
  );
}
